export const INDENT_PRIORITIES = ["Routine", "Urgent", "Emergency"] as const;
export type IndentPriority = (typeof INDENT_PRIORITIES)[number];

export const INDENT_KINDS = ["asset", "spare", "consumable"] as const;
export type IndentKind = (typeof INDENT_KINDS)[number];

export const INDENT_STATUSES = [
  "Draft", "Submitted", "HOD Approved", "Technical Review",
  "Purchase Approved", "Rejected", "PO Raised",
] as const;
export type IndentStatus = (typeof INDENT_STATUSES)[number];

// UI labels keep enum values stable but render BRD-friendly names.
export const INDENT_STATUS_LABEL: Record<IndentStatus, string> = {
  Draft: "Draft",
  Submitted: "Submitted",
  "HOD Approved": "HOD Approved",
  "Technical Review": "Technical Approved",
  "Purchase Approved": "Purchase Approved",
  Rejected: "Rejected",
  "PO Raised": "PO Raised",
};

export type IndentRow = {
  id: string;
  indent_no: string;
  branch_id: string | null;
  department_id: string | null;
  item_desc: string;
  category_id: string | null;
  qty_required: number;
  estimated_unit_cost: number;
  justification: string | null;
  remarks: string | null;
  priority: IndentPriority;
  status: IndentStatus;
  kind: IndentKind;
  // PO
  po_number: string | null;
  po_date: string | null;
  po_vendor_id: string | null;
  po_basic_value: number | null;
  po_cgst: number | null;
  po_sgst: number | null;
  po_igst: number | null;
  po_total_value: number | null;
  po_delivery_terms: string | null;
  po_expected_delivery_date: string | null;
  po_remarks: string | null;
  po_document_url: string | null;
  // Lifecycle timestamps
  submitted_at: string | null;
  hod_approved_at: string | null;
  technical_approved_at: string | null;
  purchase_approved_at: string | null;
  po_raised_at: string | null;
  grn_received_at: string | null;
  asset_created_at: string | null;
  installation_completed_at: string | null;
  closed_at: string | null;
  // Linkage
  created_asset_id: string | null;
  grn_id: string | null;
  purchase_order_id: string | null;
  // Installation
  install_engineer_name: string | null;
  install_engineer_contact: string | null;
  installation_date: string | null;
  commissioning_date: string | null;
  installation_report_url: string | null;
  training_given: boolean | null;
  training_attendees: string | null;
  installation_remarks: string | null;
  // Audit
  created_by: string | null;
  created_at: string;
  updated_at?: string;
  // Joins
  department?: { id: string; name: string } | null;
  branch?: { id: string; name: string } | null;
  category?: { id: string; name: string } | null;
  po_vendor?: { id: string; name: string } | null;
  created_asset?: { id: string; asset_code: string; name: string } | null;
  grn?: { id: string; grn_no: string } | null;
  creator?: { id: string; full_name: string | null; email: string | null; department?: { name: string } | null; branch?: { name: string } | null } | null;
  items?: PurchaseIndentItemRow[];
};

export const INDENT_ASSET_TYPES = [
  "Equipment",
  "Infrastructure",
  "Vehicle",
  "IT",
  "Furniture",
  "Other",
] as const;
export type IndentAssetType = (typeof INDENT_ASSET_TYPES)[number];

export type PurchaseIndentItemRow = {
  id: string;
  purchase_indent_id: string;
  line_no: number;
  asset_type: string | null;
  asset_category_id: string | null;
  item_name: string;
  item_description: string | null;
  quantity_required: number;
  estimated_unit_cost: number;
  estimated_total_cost: number;
  priority: IndentPriority | null;
  remarks: string | null;
  procurement_status: string;
  kind: IndentKind;
  spare_id: string | null;
  created_at: string;
  updated_at: string;
  asset_category?: { id: string; name: string } | null;
};

export type PurchaseIndentItemInput = {
  asset_type: string;
  asset_category_id?: string | null;
  item_name: string;
  item_description?: string | null;
  quantity_required: number;
  estimated_unit_cost: number;
  priority?: IndentPriority | null;
  remarks?: string | null;
  kind?: IndentKind;
  spare_id?: string | null;
};

export type IndentApproval = {
  id: string;
  indent_id: string;
  approver_role: string;
  approver_id: string | null;
  decision: "Approved" | "Rejected" | "Returned";
  remarks: string | null;
  decided_at: string;
  approver?: { full_name: string | null; email: string | null } | null;
};

export type PoPayload = {
  po_number: string;
  po_date: string;
  vendor_id: string;
  basic_value?: number | null;
  cgst?: number | null;
  sgst?: number | null;
  igst?: number | null;
  total_value?: number | null;
  delivery_terms?: string | null;
  expected_delivery_date?: string | null;
  remarks?: string | null;
  document_url?: string | null;
};

export type InstallationPayload = {
  install_engineer_name: string;
  install_engineer_contact?: string | null;
  installation_date: string;
  commissioning_date?: string | null;
  installation_report_url?: string | null;
  training_given?: boolean;
  training_attendees?: string | null;
  installation_remarks?: string | null;
};
